/**
 * Leviathan Web - Truck Form Component
 * Quasar Prime LLC
 * Reusable form for creating and editing trucks
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { parseApiError } from '@/utils/errorHandler';
import { API_URL } from '@/config/api';

interface TruckFormData {
  unitNumber: string;
  name: string;
  vin: string;
  licensePlate: string;
  state: string;
  year: string;
  make: string;
  model: string;
  color: string;
  totalCapacity: string;
  compartments: string;
  compartmentConfig: string;
  status: 'AVAILABLE' | 'IN_USE' | 'MAINTENANCE' | 'OUT_OF_SERVICE';
  odometerReading: string;
  lastServiceDate: string;
  nextServiceDue: string;
  dotInspectionDate: string;
  dotInspectionDue: string;
  registrationExpiry: string;
  insuranceExpiry: string;
  insuranceCarrier: string;
  insurancePolicyNumber: string;
  notes: string;
}

interface TruckFormProps {
  truckId?: string;
  initialData?: Partial<TruckFormData>;
  onSuccess?: () => void;
}

// Common truck makes
const TRUCK_MAKES = [
  'Peterbilt',
  'Kenworth',
  'Freightliner',
  'Mack',
  'Volvo',
  'International',
  'Western Star',
  'Hino',
  'Isuzu',
  'Ford',
  'Chevrolet',
  'RAM',
  'Other',
];

// US States
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

export default function TruckForm({ truckId, initialData, onSuccess }: TruckFormProps) {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState<TruckFormData>({
    unitNumber: '',
    name: '',
    vin: '',
    licensePlate: '',
    state: '',
    year: '',
    make: '',
    model: '',
    color: '',
    totalCapacity: '',
    compartments: '',
    compartmentConfig: '',
    status: 'AVAILABLE',
    odometerReading: '',
    lastServiceDate: '',
    nextServiceDue: '',
    dotInspectionDate: '',
    dotInspectionDue: '',
    registrationExpiry: '',
    insuranceExpiry: '',
    insuranceCarrier: '',
    insurancePolicyNumber: '',
    notes: '',
    ...initialData,
  });

  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({ ...prev, ...initialData }));
    }
  }, [initialData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Validate required fields
      if (!formData.unitNumber && !truckId) {
        // Unit number will be auto-generated if not provided for new trucks
      }

      const payload = {
        ...formData,
        year: formData.year ? parseInt(formData.year) : null,
        totalCapacity: formData.totalCapacity ? parseFloat(formData.totalCapacity) : null,
        compartments: formData.compartments ? parseInt(formData.compartments) : null,
        odometerReading: formData.odometerReading ? parseInt(formData.odometerReading) : null,
        lastServiceDate: formData.lastServiceDate || null,
        nextServiceDue: formData.nextServiceDue || null,
        dotInspectionDate: formData.dotInspectionDate || null,
        dotInspectionDue: formData.dotInspectionDue || null,
        registrationExpiry: formData.registrationExpiry || null,
        insuranceExpiry: formData.insuranceExpiry || null,
      };

      const url = truckId ? `${API_URL}/api/trucks/${truckId}` : `${API_URL}/api/trucks`;

      const response = await fetch(url, {
        method: truckId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new Error(errorMessage);
      }

      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/trucks');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save truck');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-4 bg-error-red/10 border border-error-red/50">
          <p className="text-error-red text-sm font-mono">&gt; Error: {error}</p>
        </div>
      )}

      {/* Basic Information */}
      <FormSection title="Truck Information">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <FormField label="Unit Number" required={!truckId}>
            <input
              type="text"
              name="unitNumber"
              value={formData.unitNumber}
              onChange={handleChange}
              placeholder={truckId ? '' : 'Auto-generated if blank'}
              className="form-input"
            />
          </FormField>

          <FormField label="Nickname">
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Big Red"
              className="form-input"
            />
          </FormField>

          <FormField label="Status">
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="form-input"
            >
              <option value="AVAILABLE">Available</option>
              <option value="IN_USE">In Use</option>
              <option value="MAINTENANCE">Maintenance</option>
              <option value="OUT_OF_SERVICE">Out of Service</option>
            </select>
          </FormField>

          <FormField label="VIN">
            <input
              type="text"
              name="vin"
              value={formData.vin}
              onChange={handleChange}
              placeholder="Vehicle Identification Number"
              className="form-input"
              maxLength={17}
            />
          </FormField>
        </div>
      </FormSection>

      {/* Vehicle Specifications */}
      <FormSection title="Vehicle Specifications">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <FormField label="Year">
            <input
              type="number"
              name="year"
              value={formData.year}
              onChange={handleChange}
              placeholder="e.g., 2022"
              min="1900"
              max={new Date().getFullYear() + 1}
              className="form-input"
            />
          </FormField>

          <FormField label="Make">
            <select
              name="make"
              value={formData.make}
              onChange={handleChange}
              className="form-input"
            >
              <option value="">-- Select Make --</option>
              {TRUCK_MAKES.map((make) => (
                <option key={make} value={make}>
                  {make}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Model">
            <input
              type="text"
              name="model"
              value={formData.model}
              onChange={handleChange}
              placeholder="e.g., 389"
              className="form-input"
            />
          </FormField>

          <FormField label="Color">
            <input
              type="text"
              name="color"
              value={formData.color}
              onChange={handleChange}
              placeholder="e.g., White"
              className="form-input"
            />
          </FormField>

          <FormField label="License Plate">
            <input
              type="text"
              name="licensePlate"
              value={formData.licensePlate}
              onChange={handleChange}
              placeholder="Plate number"
              className="form-input"
            />
          </FormField>

          <FormField label="Plate State">
            <select
              name="state"
              value={formData.state}
              onChange={handleChange}
              className="form-input"
            >
              <option value="">-- Select State --</option>
              {US_STATES.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Odometer (miles)">
            <input
              type="number"
              name="odometerReading"
              value={formData.odometerReading}
              onChange={handleChange}
              placeholder="Current mileage"
              min="0"
              className="form-input"
            />
          </FormField>
        </div>
      </FormSection>

      {/* Capacity & Compartments */}
      <FormSection title="Capacity & Compartments">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <FormField label="Total Capacity (gallons)">
            <input
              type="number"
              name="totalCapacity"
              value={formData.totalCapacity}
              onChange={handleChange}
              placeholder="e.g., 5000"
              step="0.01"
              min="0"
              className="form-input"
            />
          </FormField>

          <FormField label="Number of Compartments">
            <input
              type="number"
              name="compartments"
              value={formData.compartments}
              onChange={handleChange}
              placeholder="e.g., 4"
              min="1"
              max="10"
              className="form-input"
            />
          </FormField>

          <FormField label="Compartment Configuration">
            <input
              type="text"
              name="compartmentConfig"
              value={formData.compartmentConfig}
              onChange={handleChange}
              placeholder="e.g., 1500/1500/1000/1000"
              className="form-input"
            />
          </FormField>
        </div>
      </FormSection>

      {/* Maintenance & Service */}
      <FormSection title="Maintenance & Service">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <FormField label="Last Service Date">
            <input
              type="date"
              name="lastServiceDate"
              value={formData.lastServiceDate}
              onChange={handleChange}
              className="form-input"
            />
          </FormField>

          <FormField label="Next Service Due">
            <input
              type="date"
              name="nextServiceDue"
              value={formData.nextServiceDue}
              onChange={handleChange}
              className="form-input"
            />
          </FormField>

          <FormField label="DOT Inspection Date">
            <input
              type="date"
              name="dotInspectionDate"
              value={formData.dotInspectionDate}
              onChange={handleChange}
              className="form-input"
            />
          </FormField>

          <FormField label="DOT Inspection Due">
            <input
              type="date"
              name="dotInspectionDue"
              value={formData.dotInspectionDue}
              onChange={handleChange}
              className="form-input"
            />
          </FormField>
        </div>
      </FormSection>

      {/* Compliance */}
      <FormSection title="Compliance & Registration">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField label="Registration Expiry">
            <input
              type="date"
              name="registrationExpiry"
              value={formData.registrationExpiry}
              onChange={handleChange}
              className="form-input"
            />
          </FormField>

          <FormField label="Insurance Expiry">
            <input
              type="date"
              name="insuranceExpiry"
              value={formData.insuranceExpiry}
              onChange={handleChange}
              className="form-input"
            />
          </FormField>

          <FormField label="Insurance Carrier">
            <input
              type="text"
              name="insuranceCarrier"
              value={formData.insuranceCarrier}
              onChange={handleChange}
              placeholder="e.g., Progressive Commercial"
              className="form-input"
            />
          </FormField>

          <FormField label="Insurance Policy Number">
            <input
              type="text"
              name="insurancePolicyNumber"
              value={formData.insurancePolicyNumber}
              onChange={handleChange}
              placeholder="e.g., POL-2024-00123"
              className="form-input"
            />
          </FormField>
        </div>
      </FormSection>

      {/* Notes */}
      <FormSection title="Notes">
        <FormField label="Additional Notes">
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={4}
            placeholder="Any additional notes about this truck..."
            className="form-input resize-none"
          />
        </FormField>
      </FormSection>

      {/* Action Buttons */}
      <div className="flex items-center justify-end space-x-4 pt-4">
        <button
          type="button"
          onClick={() => navigate('/trucks')}
          className="px-6 py-3 bg-panel-border hover:bg-steel/20 text-text-primary font-mono uppercase text-sm tracking-wider transition-all border border-panel-border hover:border-steel"
        >
          Cancel
        </button>

        <button
          type="submit"
          disabled={isLoading}
          className="px-8 py-3 bg-amber hover:bg-amber/90 text-background font-display font-bold uppercase tracking-wider transition-all disabled:opacity-50 disabled:cursor-not-allowed relative group"
          style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
        >
          <span className="relative z-10">
            {isLoading ? 'Saving...' : truckId ? 'Update Truck' : 'Create Truck'}
          </span>
          <div
            className="absolute inset-0 bg-gradient-to-r from-amber to-steel opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ mixBlendMode: 'overlay' }}
          />
        </button>
      </div>
    </form>
  );
}

function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
        <div className="flex items-center space-x-2 mb-6">
          <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
          <h3 className="text-xl font-display text-text-primary uppercase tracking-wide">
            {title}
          </h3>
        </div>
        {children}
      </div>
    </div>
  );
}

function FormField({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
        {label} {required && <span className="text-amber">*</span>}
      </label>
      {children}
    </div>
  );
}
